import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import { config } from '../config';

interface SearchMessage {
  id: string;
  thread_id: string;
  content: string;
  user_id: string;
  user_email: string;
  user_name: string;
  created_at: string;
  modified_at: string;
  role: string;
  subtype?: string;
  diary_id: string;
  threadTitle?: string;
  highlightedContent: string;
  highlightedTitle?: string;
}

interface SearchBarProps {
  placeholder?: string;
  onResultClick?: (result: SearchMessage) => void;
  onSearchResultsChange?: (results: SearchMessage[], query: string) => void;
  maxResults?: number;
  debounceMs?: number;
  className?: string;
}

// Utility function to parse highlighted text and convert **abcxyz** to HTML highlighting
export const parseHighlightedText = (text: string): string => {
  if (!text) return '';
  
  // Replace **text** with <mark>text</mark> for highlighting
  return text.replace(/\*\*(.*?)\*\*/g, '<mark style="background-color: #FFD700; padding: 1px 2px; border-radius: 2px;">$1</mark>');
};

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  onResultClick,
  onSearchResultsChange,
  maxResults = 500,
  debounceMs = 300,
  className,
}) => {
  const { t } = useTranslation();
  const [activeOrganisation] = useActiveOrganisation();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const searchControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search effect
  useEffect(() => {
    if (!activeOrganisation?.organizationId) return;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      // Only call API if query is at least 1 char
      if (!query.trim() || query.length < 1) {
        setSearchResults([]);
        onSearchResultsChange?.([], query);
        return;
      }

      // Cancel previous request if it exists
      searchControllerRef.current?.abort();
      const abortController = new AbortController();
      searchControllerRef.current = abortController;
      setIsLoading(true);
      setError(null);

      (async () => {
        try {
          // Get access token
          const tokenResponse = await fetch('/access-token');
          const { accessToken } = await tokenResponse.json();

          const url = new URL(config.endpoints.searchMessages);
          url.searchParams.append('q', query);
          url.searchParams.append('limit', maxResults.toString());

          const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              'X-Organization': activeOrganisation.organizationId,
            },
            signal: abortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Search request failed: ${response.status}`);
          }

          const results: SearchMessage[] = await response.json();
          setSearchResults(results);
          onSearchResultsChange?.(results, query);
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('Error searching messages:', error);
            setError(error.message);
            setSearchResults([]);
            onSearchResultsChange?.([], query);
          }
        } finally {
          setIsLoading(false);
        }
      })();
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [query, debounceMs, maxResults, activeOrganisation?.organizationId]);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleClearQuery = () => {
    setQuery('');
    setSearchResults([]);
    onSearchResultsChange?.([], '');
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={placeholder || t('Search messages...')}
        value={query}
        onChange={handleQueryChange}
        className={className}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: 'rgba(56, 39, 103, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(56, 39, 103, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#382767',
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#382767' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading && (
                <CircularProgress size={20} sx={{ color: '#382767' }} />
              )}
              {query && !isLoading && (
                <Tooltip title={t('Clear search')}>
                  <IconButton
                    size="small"
                    onClick={handleClearQuery}
                    sx={{ color: '#382767' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default SearchBar; 